import {
  CircularProgress as MuiCircularProgress,
  type CircularProgressProps as MuiCircularProgressProps,
} from '@mui/material';
import { LoadingSpinner } from '@figma/fpl-components';
import { forwardRef, type ReactNode } from 'react';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';

/** FPL `LoadingSpinner` preset sizes. `sm` = 16px, `md`/`lg` = 24px (`lg` uses a heavier glyph). */
export type LoadingSpinnerSize = 'sm' | 'md' | 'lg';

/**
 * Snap a legacy numeric (or `"16px"`-style string) spinner size onto the nearest FPL preset.
 * FPL caps at 24px, so anything larger clamps to `lg`. Shared with `LoadingCircle`.
 *
 * - `<= 16` → `sm` (16px)
 * - `<= 24` → `md` (24px)
 * - `> 24`  → `lg` (24px, heavier glyph)
 * - `undefined` / unparseable → `fallback` (the migrated component's legacy default size)
 */
export function snapSpinnerSize(size: number | string | undefined, fallback: LoadingSpinnerSize): LoadingSpinnerSize {
  if (size == null) {
    return fallback;
  }
  const px = typeof size === 'number' ? size : Number.parseFloat(size);
  if (Number.isNaN(px)) {
    return fallback;
  }
  if (px <= 16) {
    return 'sm';
  }
  if (px <= 24) {
    return 'md';
  }
  return 'lg';
}

/**
 * FPL-native configuration for the `fpl-migration`-on branch. Callers pass it without checking the
 * flag; the legacy MUI branch ignores the entire bucket. Use it only when the flag-on branch needs
 * configuration that cannot be mapped mechanically from the MUI API (an explicit preset size or an
 * accessible label). Color and layout are FPL's / the consumer's responsibility.
 */
export interface CircularProgressFplProps {
  /** Explicit FPL preset; overrides the automatic snap from the legacy numeric `size`. */
  size?: LoadingSpinnerSize;
  /** Screen-reader announcement text; FPL defaults to "Loading" when omitted. */
  loadingText?: ReactNode;
}

export type CircularProgressProps = MuiCircularProgressProps & {
  /** Configuration read only by the `fpl-migration`-on branch; ignored by the legacy MUI branch. */
  fplProps?: CircularProgressFplProps;
};

/**
 * Indeterminate circular loading spinner
 *
 * Branches on fpl-migration flag:
 * - Flag off: passthrough to legacy MUI `CircularProgress`
 * - Flag on: FPL's `LoadingSpinner` with FPL's native color and layout.
 *   `size` snaps to the nearest FPL preset (default `lg`, matching the MUI 40px default).
 *   `color`, `sx`, `thickness`, and determinate `variant`/`value` are legacy-only
 *   the flag-on spinner takes no color/layout
 *
 *   overrides; use `fplProps` only for an explicit preset size or accessible label.
 */
export const CircularProgress = forwardRef<HTMLSpanElement, CircularProgressProps>(function CircularProgress(
  { fplProps, ...muiProps },
  ref,
) {
  const isFplMigration = useFplMigrationFlag();

  if (isFplMigration === true) {
    return (
      <LoadingSpinner
        size={fplProps?.size ?? snapSpinnerSize(muiProps.size, 'lg')}
        loadingText={fplProps?.loadingText}
      />
    );
  }

  return <MuiCircularProgress ref={ref} {...muiProps} />;
});
